import api from './client';
import { EntitlementsStatus } from './billing';

export interface AdminInstance {
  id: string;
  name: string;
  plan: string;
  environment: string;
  version: string;
  configured: boolean;
}

export interface BackupStatus {
  enabled: boolean;
  cron: string;
  retention: number;
  destination: 'object-storage' | 'local';
  running: boolean;
  last: {
    ok: boolean;
    at: string | null;
    name: string | null;
    sizeBytes: number | null;
    error: string | null;
  };
}

export interface AdminOverview {
  instance: AdminInstance;
  storage: { objectStorage: boolean; storageRoot: string | null };
  observability: { sentry: boolean };
  mail: {
    configured: boolean;
    simulate?: boolean;
    provider?: string;
    host?: string;
    port?: number;
    from?: string;
  };
  backups: BackupStatus;
  vault?: {
    replicationEnabled: boolean;
    localEnabled: boolean;
    remoteIngest: boolean;
    retentionDays: number;
    lastReplication: {
      ok: boolean;
      at: string | null;
      backupId: string | null;
      error: string | null;
    };
  };
  subscription: EntitlementsStatus;
}

export interface BackupRecord {
  name: string;
  location: 'object-storage' | 'local';
  sizeBytes: number;
  createdAt: string;
}

export interface AiUsageBreakdown {
  key: string;
  requestCount: number;
  totalTokens: number;
  estimatedCostUsd: number;
  averageLatencyMs: number;
}

export interface AiUsagePlanContext {
  key: string;
  name: string;
  priceMonthlyUsd: number;
  aiInvocationsPerMonth: number | null;
  indicativeAiCostUsdMonthly: number;
  billingConfigured: boolean;
}

export interface AiBillingMonthUsage {
  start: string;
  end: string;
  label: string;
  daysElapsed: number;
  daysInMonth: number;
  requestCount: number;
  billableInvocations: number;
  cacheHits: number;
  failedRequests: number;
  totalTokens: number;
  promptTokens: number;
  completionTokens: number;
  estimatedCostUsd: number;
  averageLatencyMs: number;
  invocationLimit: number | null;
  remainingInvocations: number | null;
  percentOfInvocationLimit: number | null;
  percentOfIndicativeBudget: number | null;
  projectedInvocations: number;
  projectedCostUsd: number;
  withinInvocationLimit: boolean;
  withinIndicativeBudget: boolean;
  byFeature: AiUsageBreakdown[];
  byModel: AiUsageBreakdown[];
  daily: Array<{
    date: string;
    requestCount: number;
    totalTokens: number;
    estimatedCostUsd: number;
  }>;
}

export interface AiUsageSummary {
  period: { days: number; start: string; end: string };
  totals: {
    requestCount: number;
    successfulRequests: number;
    failedRequests: number;
    cacheHits: number;
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
    estimatedCostUsd: number;
    averageLatencyMs: number;
  };
  byFeature: AiUsageBreakdown[];
  byModel: AiUsageBreakdown[];
  daily: Array<{
    date: string;
    requestCount: number;
    totalTokens: number;
    estimatedCostUsd: number;
  }>;
  plan?: AiUsagePlanContext;
  billingMonth?: AiBillingMonthUsage;
}

export const adminApi = {
  getOverview: async (): Promise<AdminOverview> => {
    const res = await api.get<AdminOverview>('admin/overview');
    return res.data;
  },
  getAiUsage: async (days = 30): Promise<AiUsageSummary> => {
    const res = await api.get<AiUsageSummary>('admin/ai-usage', {
      days: String(days),
    });
    return res.data;
  },
  listBackups: async (): Promise<BackupRecord[]> => {
    const res = await api.get<BackupRecord[]>('admin/backups');
    return res.data;
  },
  runBackup: async (): Promise<BackupRecord> => {
    const res = await api.post<BackupRecord>('admin/backup/run', {});
    return res.data;
  },
  sendTestEmail: async (): Promise<{
    sent: boolean;
    to?: string;
    message: string;
    simulate?: boolean;
  }> => {
    const res = await api.post<{ sent: boolean; to?: string; message: string }>(
      'admin/test-email',
      {},
    );
    return res.data;
  },
};
